import type {
  EmployeeStampProgress,
  PassportCatalogue,
  PassportPageSummary,
} from "@peer-start/platform-contracts";
import { BadgeCheck, Circle, LoaderCircle } from "lucide-react";
import type { LucideIcon } from "lucide-react";

export type CompetencyTone = "success" | "warning" | "info" | "muted";

export type ProfileSkillRow = {
  id: string;
  title: string;
  percent: number;
  pageIds: string[];
};

/** Show a required-scope tag only when the competency has required elements. */
export function competencyScopeLabel(
  summary: PassportPageSummary,
): { key: string; params: Record<string, number> } | null {
  if (summary.required <= 0) return null;
  return { key: "profile.competencyScopeRequired", params: { count: summary.required } };
}

export function competencyStatus(
  summary: PassportPageSummary,
  progressList: EmployeeStampProgress[],
): {
  labelKey: string;
  labelParams?: Record<string, number>;
  tone: CompetencyTone;
  Icon: LucideIcon;
} {
  const progressByStamp = new Map(progressList.map((entry) => [entry.stampId, entry]));
  const scoped: EmployeeStampProgress[] = [];
  for (const stampId of summary.page.stampIds) {
    const entry = progressByStamp.get(stampId);
    if (!entry || entry.applicability === "out_of_scope") continue;
    scoped.push(entry);
  }
  const total = scoped.length || summary.required + summary.optional;
  const confirmed = scoped.filter((entry) => entry.status === "established").length;
  const started = scoped.filter((entry) => entry.status !== "unassessed").length;

  if (total > 0 && confirmed >= total) {
    return { labelKey: "profile.competencyAchieved", tone: "success", Icon: BadgeCheck };
  }
  if (started > 0) {
    return {
      labelKey: "profile.competencyInProgress",
      labelParams: { confirmed, total },
      tone: "info",
      Icon: LoaderCircle,
    };
  }
  return { labelKey: "profile.competencyNotStarted", tone: "muted", Icon: Circle };
}

export const competencyToneClasses: Record<CompetencyTone, string> = {
  success: "bg-success text-success-foreground",
  warning: "bg-warning text-warning-foreground",
  info: "bg-info text-info-foreground",
  muted: "bg-muted text-muted-foreground",
};

function requirementPercent(score: number, state: "covered" | "partial" | "uncovered"): number {
  if (state === "covered") return 100;
  if (state === "partial") return Math.max(1, Math.round((score / 3) * 100));
  return 0;
}

export function skillProgressForPage(
  pageId: string,
  skills: ProfileSkillRow[],
): { completed: number; total: number } {
  const scoped = skills.filter((skill) => skill.pageIds.includes(pageId));
  return {
    completed: scoped.filter((skill) => skill.percent >= 100).length,
    total: scoped.length,
  };
}

export function buildProfileSkillRows(
  catalogue: PassportCatalogue,
  progressList: EmployeeStampProgress[],
): ProfileSkillRow[] {
  const progressByRequirement = new Map<string, number>();
  const pageIdsByStamp = new Map(catalogue.stamps.map((stamp) => [stamp.id, stamp.pageIds] as const));

  for (const progress of progressList) {
    if (progress.applicability === "out_of_scope") continue;
    for (const requirement of progress.atomicCoverage.requirements) {
      if (requirement.kind !== "skill") continue;
      const percent = requirementPercent(requirement.score, requirement.state);
      if (percent <= 0) continue;
      const previous = progressByRequirement.get(requirement.requirementId) ?? 0;
      if (percent > previous) progressByRequirement.set(requirement.requirementId, percent);
    }
  }

  return catalogue.criteria
    .filter((criterion) => criterion.kind === "skill" && !criterion.archived)
    .map((criterion) => {
      const percent = progressByRequirement.get(criterion.id) ?? 0;
      const pageIds = [
        ...new Set(criterion.stampIds.flatMap((stampId) => pageIdsByStamp.get(stampId) ?? [])),
      ];
      return {
        id: criterion.id,
        title: criterion.title,
        percent,
        pageIds,
      };
    })
    .sort((left, right) => right.percent - left.percent || left.title.localeCompare(right.title));
}
